import _ from "lodash";
import { CSSProperties, FC, useEffect, useState } from 'react';
import { 
  MenuItem, FormControl, InputLabel, Box, Typography,
  Select, TextField, FormHelperText, Grid, Divider,
  InputBaseComponentProps
} from "@mui/material"

import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { NumericFormatField } from "@gisce/oficina-virtual-components";
import { useTipovias } from "@/components/form-fields/AddressForm/hooks/useTipovias";
import { usePoblacionsINE } from "./hooks/usePoblacionsINE";
import { useProvincies } from "./hooks/useProvincies";
import { Address } from "./types/address";

interface IAddressForm {
  readOnly?: boolean;
  showMobile?: boolean;
  showEmail?: boolean;
  showTitle?: boolean;
  showRefCatastral?: boolean;
  showName?: boolean;
  label?: JSX.Element;
  name?: string;
  style?: CSSProperties;
  titleType?: "h6" | "subtitle1"
  // fieldsInputProps?: Record<keyof Address, InputProps>
  fieldsInputProps?: {[key in keyof Address]?: InputBaseComponentProps}
}

const AddressForm: FC<IAddressForm> = ({
  readOnly = false,
  label = '',
  showMobile = false,
  showEmail = false,
  showTitle = false,
  showRefCatastral = true,
  // showName = false
  name = "address",
  titleType = "h6",
  style = {},
  fieldsInputProps
}) => {
  const { t } = useTranslation();

  const { 
    control,
    watch,
    setValue,
    getValues,
    trigger
  } = useFormContext();

  // const [debouncedDp, setDebouncedDp] = useState<Address["dp"]>(getValues(`${name}.dp`)); 

  const watchAddress: Address = watch(name);

  const tipoviasData = useTipovias();
  const poblacionsINEData = usePoblacionsINE(watchAddress.dp);
  const provinciesData = useProvincies(watchAddress.ine.slice(0, 2));

  // useEffect(() => {
  //   // Debouncer when dp is entered
  //   if (debouncedDp !== '') {
  //     const debounceForm = setTimeout(() => {
  //       setValue(`${name}.dp`, debouncedDp);
  //     }, 1000);
      
  //     return () => clearTimeout(debounceForm);
  //   } else {
  //     setValue(`${name}.dp`, debouncedDp);
  //   }
  // }, [debouncedDp]);

  // Població retorna dades
  useEffect(() => {
    if (poblacionsINEData.isSuccess) {
      if (poblacionsINEData.data.length == 0) {
        setValue(`${name}.poblacio`, '');
      } else if (poblacionsINEData.data.length == 1) {
        setValue(`${name}.poblacio`, poblacionsINEData.data[0].city);
        trigger(`${name}.poblacio`)
      } else if (poblacionsINEData.data.length > 1) {
        setValue(`${name}.poblacio`, '');
        setValue(`${name}.state`, '');
        setValue(`${name}.ine`, '');
      }
    }
  }, [poblacionsINEData.isSuccess, poblacionsINEData.data])

  // INE
  useEffect(() => {
    if (watchAddress?.poblacio && poblacionsINEData.isSuccess && poblacionsINEData.data.length) {
      const newINE = poblacionsINEData.data.find(pobINE => pobINE.city == watchAddress.poblacio)?.ine ?? "";
      setValue(`${name}.ine`, newINE);
    }
  }, [watchAddress?.poblacio, poblacionsINEData.isSuccess, poblacionsINEData.data]);

  // State
  useEffect(() => {
    if (provinciesData.isSuccess && provinciesData.data.length == 1) {
      if (watchAddress?.poblacio && !watchAddress?.state) {
        setValue(`${name}.state`, provinciesData.data[0].id);
        trigger(`${name}.state`);
      } else if (watchAddress.state !== provinciesData.data[0].id) {
        setValue(`${name}.state`, '');
      }
    } else {
      setValue(`${name}.state`, '');
    }
  }, [provinciesData.isSuccess, provinciesData.data, watchAddress?.poblacio])

  const renderAddress = () => {

    if (_.isEmpty(watchAddress)) {
      return <div/>
    }

    return (
      <div>
        {showTitle &&
          <Typography variant={titleType}>
            {t('common:text.contractation_address')}
          </Typography>
        }
        <div>{tipoviasData.isSuccess && tipoviasData.data.find(tv => tv.id == watchAddress.tv)?.name } { watchAddress.nv }, { watchAddress.pnp } {watchAddress.bq} {watchAddress.es} {watchAddress.pt} {watchAddress.pu}</div>
        <div>{watchAddress.dp} { watchAddress.poblacio } ({provinciesData.isSuccess && provinciesData.data.find(prov => prov.id == watchAddress.state)?.name})</div>
        { watchAddress.ref_catastral && showRefCatastral &&
          <div>{ t('common:text.address_ref') } { watchAddress.ref_catastral }</div>
        }
      </div>
    );
  }

  const possiblePoblacionsINE = () => {
    if (poblacionsINEData.isFetching && watchAddress.poblacio) {
      return <MenuItem key={0} value={watchAddress.poblacio}>{watchAddress.poblacio}</MenuItem>
    }

    if (poblacionsINEData.isSuccess && poblacionsINEData.data.length) {
      return poblacionsINEData.data.map((pob, idx) => {
        return <MenuItem key={idx} value={pob.city}>{pob.city}</MenuItem>
      })
    }
  }

  const renderForm = () => {

    const possibleProvincies = provinciesData.isSuccess && provinciesData.data.map((prov, idx) => {
      return <MenuItem key={idx} value={prov.id}>{prov.name}</MenuItem>
    });

    const possibleTipovias = tipoviasData.isSuccess && tipoviasData.data.map((tipovia, idx) => {
      return <MenuItem key={idx} value={tipovia.id}>{tipovia.name}</MenuItem>
    });

    return (
      <div>
        {(showMobile || showEmail) &&
          <>
            {!label &&
              <Typography variant="subtitle1" style={{marginTop: 20}}>
                {t('common:text.contractation_contact_details')}
              </Typography>
            }
            <Grid container spacing={3}>
              {showMobile && (
                <Grid item xs={12} sm={6} >
                  <Controller
                    name={`${name}.mobile`}
                    control={control}
                    render={({ field, fieldState }) => (<>
                      <TextField
                        {...field}
                        label={t('common:text.address_phone')}
                        style={{ width: "100%" }}
                        error={!!(fieldState.error)}
                        inputProps={fieldsInputProps?.mobile}
                      />
                      {fieldState.error?.message && (
                        <FormHelperText error={true}>
                          {t(fieldState.error.message)}
                        </FormHelperText>
                      )}
                    </>)}
                  />
                </Grid>
              )}
              {showEmail && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`${name}.email`}
                    control={control}
                    render={({ field, fieldState }) => (<>
                      <TextField
                        {...field}
                        label={t('common:text.address_email')}
                        style={{ width: "100%" }}
                        error={!!(fieldState.error)}
                        inputProps={fieldsInputProps?.email}
                      />
                      {fieldState.error?.message && (
                        <FormHelperText error={true}>
                          {t(fieldState.error.message)}
                        </FormHelperText>
                      )}
                    </>)}
                  />
                </Grid>
              )}
            </Grid>
            <Divider style={{marginBottom: 20, marginTop: 20}}/>
          </>
        }
        {/* {showName && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('common:text.address_name')}
                name="name"
                fullWidth
                onChange={handleGenericOnChange}
                value={address?.name}
                error={!!errors?.name}
              />
              {errors?.name && (
                <FormHelperText error={true}>{errors.name}</FormHelperText>
              )}
            </Grid>
          </Grid>
        )} */}
        { !label &&
        <Typography variant={titleType}>
          {t('common:text.contractation_address')}
        </Typography>
        }
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.dp`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  InputProps={{
                    inputComponent: NumericFormatField,
                    inputProps: {
                      allowNegative: false,
                      allowLeadingZeros: true,
                      decimalScale: 0,
                      ...fieldsInputProps?.dp
                    }
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setValue(`${name}.dp`, e.target.value)}}
                  label={t('common:text.address_postal_code')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Controller
              name={`${name}.poblacio`}
              control={control}
              render={({field, fieldState}) => (<FormControl
                  style={{ width: "100%" }}
                >
                  <InputLabel shrink htmlFor="">
                    {t('common:text.address_locality')}
                  </InputLabel>

                  <Select
                    {...field}
                    // placeholder={}
                    // value={Array.isArray(possiblePoblacionsINE) && possiblePoblacionsINE.length > 0 ? field.value : ''}
                    value={field.value}
                    disabled={poblacionsINEData.isLoading}
                    error={!!(fieldState.error)}
                    autoWidth={true}
                    inputProps={fieldsInputProps?.poblacio}
                  >
                    {possiblePoblacionsINE()}
                  </Select>

                  {fieldState.error?.message && (
                    <FormHelperText error={true}>
                      {t(fieldState.error.message)}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.state`}
              control={control}
              render={({field, fieldState}) => (<FormControl
                  style={{ width: "100%" }}
                >
                  <InputLabel shrink htmlFor="">
                    {t('common:text.address_province')}
                  </InputLabel>

                  <Select
                    {...field}
                    value={Array.isArray(possibleProvincies) && possibleProvincies.length > 0 ? field.value : ''}
                    disabled={true}
                    autoWidth={true}
                    error={!!(fieldState.error)}
                    inputProps={fieldsInputProps?.state}
                  >
                    {possibleProvincies}
                  </Select>

                  {fieldState.error?.message && (
                    <FormHelperText error={true}>
                      {t(fieldState.error.message)}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.tv`}
              control={control}
              render={({field, fieldState}) => (<FormControl
                  style={{ width: "100%" }}
                >
                  <InputLabel shrink htmlFor="" id="tvLabel">
                    {t('common:text.address_street_type')}
                  </InputLabel>

                  <Select
                    {...field}
                    value={Array.isArray(possibleTipovias) && possibleTipovias.length > 0 ? field.value : ''}
                    disabled={tipoviasData.isLoading || tipoviasData.isError}
                    error={!!(fieldState.error)}
                    autoWidth={true}
                    labelId="tvLabel" // Necessary for testing: https://v6.mui.com/material-ui/react-select/#accessibility
                    role="dialog"
                    inputProps={fieldsInputProps?.tv}
                  >
                    {possibleTipovias}
                  </Select>

                  {fieldState.error?.message && (
                    <FormHelperText error={true}>
                      {t(fieldState.error.message)}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={9} md={6}>
            <Controller
              name={`${name}.nv`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_street_name')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  inputProps={fieldsInputProps?.nv}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Controller
              name={`${name}.pnp`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_street_number')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  inputProps={fieldsInputProps?.pnp}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.bq`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_block')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  inputProps={fieldsInputProps?.bq}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.es`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_stairs')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  inputProps={fieldsInputProps?.es}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.pt`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_floor')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  inputProps={fieldsInputProps?.pt}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.pu`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_door')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  inputProps={fieldsInputProps?.pu}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          {showRefCatastral &&
            <Grid item xs={12}>
              <Controller
                name={`${name}.ref_catastral`}
                control={control}
                render={({ field, fieldState }) => (<>
                  <TextField
                    {...field}
                    onChange={(event) => {
                      field.onChange(
                        event.target.value.replace(/\s/g, "").toUpperCase()
                      )
                    }}
                    label={t('common:text.address_ref')}
                    style={{ width: "100%" }}
                    error={!!(fieldState.error)}
                    inputProps={fieldsInputProps?.ref_catastral}
                  />
                  {fieldState.error?.message && (
                    <FormHelperText error={true}>
                      {t(fieldState.error.message)}
                    </FormHelperText>
                  )}
                </>)}
              />
            </Grid>
          }
          <Grid item xs={12}>
            <Controller
              name={`${name}.aclarador`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_hint')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  variant="filled"
                  minRows={2}
                  multiline
                  inputProps={fieldsInputProps?.aclarador}
                />
                {fieldState.error?.message && (
                  <FormHelperText error={true}>
                    {t(fieldState.error.message)}
                  </FormHelperText>
                )}
              </>)}
            />
            <div
              className="helper-text" 
              style={{ width: "100%", marginTop: 5 }}
            >
              {t('common:text.address_hint_helper')}
            </div>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div style={style}>
      {label &&
        <Box>
          <Typography variant="h6">
            {label}
          </Typography>
        </Box>
      }
      {!readOnly && renderForm()}
      {readOnly && renderAddress()}

    </div>
  );
}

export default AddressForm;
